import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useSession } from './lib/session.js';
import { LoadingScreen } from './components/ui.js';
import { AppLayout } from './components/shell/AppLayout.js';

import { AgentHubPage } from './pages/AgentHubPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { SignupPage } from './pages/SignupPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { CalendarPage } from './pages/CalendarPage.js';
import { InquiriesPage } from './pages/InquiriesPage.js';
import { CallsPage } from './pages/CallsPage.js';
import { QuotesPage } from './pages/QuotesPage.js';
import { QuoteEditorPage } from './pages/QuoteEditorPage.js';
import { QuoteDetailPage } from './pages/QuoteDetailPage.js';
import { RemindersPage } from './pages/RemindersPage.js';
import { NotificationsPage } from './pages/NotificationsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { MiniSitePage } from './pages/MiniSitePage.js';
import { PublicQuotePage } from './pages/PublicQuotePage.js';
import { SigningEntryPage } from './pages/SigningEntryPage.js';
import { PublicSignPage } from './pages/PublicSignPage.js';
import { SigningWorkspace } from './pages/signing/index.js';
import { SigningCompose } from './pages/signing/Compose.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { PublicMiniSitePage } from './pages/PublicMiniSitePage.js';
import { LandingPage } from './pages/LandingPage.js';
import { LegalPage } from './pages/LegalPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { CustomerPaymentsPage } from './pages/payments/CustomerPaymentsPage.js';
import { PublicCustomerPaymentPage } from './pages/payments/PublicCustomerPaymentPage.js';
import { PlatformPage } from './pages/platform/index.js';

/**
 * Sends signed-out visitors to login, preserving where they were heading.
 *
 * Except at the root, which is the one address a stranger reaches without
 * having been sent: someone who has never signed up should be met by what the
 * product is (MKT-001), not by a form asking for a password they do not have.
 */
function Protected({ children }: { children: ReactElement }) {
  const { session, loading, unreachable } = useSession();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  // Only a definite "no session" sends the user to login. If the API simply
  // could not be reached, the cookie is probably still good — show the loader
  // rather than discarding a valid session on a blip.
  if (unreachable) return <LoadingScreen />;
  if (!session) {
    if (location.pathname === '/') return <Navigate to="/welcome" replace />;
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/** Signed-in users have no reason to see the auth screens. */
function GuestOnly({ children }: { children: ReactElement }) {
  const { session, loading } = useSession();
  if (loading) return <LoadingScreen />;
  if (session) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      {/* Customer-facing, no session (QUO-009). */}
      <Route path="/q/:token" element={<PublicQuotePage />} />
      <Route path="/pay/:token" element={<PublicCustomerPaymentPage />} />
      <Route path="/sign/:token" element={<PublicSignPage />} />
      <Route path="/sign-entry/:token" element={<SigningEntryPage />} />
      <Route path="/s/:slug" element={<PublicMiniSitePage />} />

      {/* MKT-001 — what the product is, for anyone who has not signed up yet.
          Staff who are signed in get the same page with its editor on it. */}
      <Route path="/welcome" element={<LandingPage />} />

      {/* LEGAL-001 — outside every guard. Google Play and the OAuth consent
          screen link here from outside the product, and a reviewer met by a
          sign-in form reads the link as broken. */}
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/accessibility" element={<LegalPage kind="accessibility" />} />
      {/* ACCT-DEL — Play requires a URL that explains account deletion and is
          reachable without installing the app, so it sits beside the other two
          rather than behind the sign-in form it is describing how to leave. */}
      <Route path="/delete-account" element={<LegalPage kind="deleteAccount" />} />

      <Route
        path="/login"
        element={
          <GuestOnly>
            <LoginPage />
          </GuestOnly>
        }
      />
      <Route
        path="/signup"
        element={
          <GuestOnly>
            <SignupPage />
          </GuestOnly>
        }
      />
      <Route
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/agent" element={<AgentHubPage />} />
        <Route path="/mini-site" element={<MiniSitePage />} />
        <Route path="/inquiries" element={<InquiriesPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        {/*
          QUO-1.4 — a new quote opens over the send history, not instead of it.

          Both, at the same address. The popup is not a piece of navigation
          state that only exists if you arrived by pressing the button: a link
          to `/quotes/new` is a link people already have, and landing on it
          directly has to draw the same thing — the list, with the form on top
          of it. So the route renders the pair.

          Closing the form is a step back to whatever screen opened it (1.2),
          not a push of the list: a push left the list twice in the back stack
          with the form between them. Only a form landed on directly, with
          nothing of the app's below it, gives way to `/quotes` in place.
        */}
        <Route
          path="/quotes/new"
          element={
            <>
              <QuotesPage />
              <QuoteEditorPage asDialog />
            </>
          }
        />
        {/* Editing an existing quote stays a screen. It is reached from the
            quote itself rather than from the list, and there is nothing behind
            it for a popup to be over. */}
        <Route path="/quotes/:id/edit" element={<QuoteEditorPage />} />
        <Route path="/quotes/:id" element={<QuoteDetailPage />} />
        <Route path="/reminders" element={<RemindersPage />} />
        <Route path="/activity" element={<Navigate to="/notifications?tab=activities" replace />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        {/* The call log is its own screen again. It spent a while as the middle
            tab of the enquiries page, where it sat between the two lists anyone
            had come to that screen for and answered a question neither of them
            asked. It is also where the dashboard's call figures link to, with a
            direction in the query string — a redirect swallowed that. */}
        <Route path="/calls" element={<CallsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        {/* The team list is a card on the account screen now, and the one
            sentence about signing in sits in its settings. Nothing is left here
            to render. */}
        <Route path="/admin" element={<Navigate to="/profile" replace />} />
        {/* The provider's success and cancel URLs both land here (AUTH-009). */}
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/customer-payments" element={<CustomerPaymentsPage />} />
        {/* Worki's own staff screen (module 13). The route stays declared for
            everyone — the API answers 404 to anyone who is not staff, which is
            the only gate that matters; hiding it here would only hide the nav
            entry, which AppShell already does. */}
        <Route path="/platform" element={<PlatformPage />} />
        <Route path="/platform/:section" element={<PlatformPage />} />

        {/* The paths the reference build used for the same screens. Kept as
            redirects so a link or bookmark from it lands somewhere. */}
        <Route path="/leads" element={<Navigate to="/inquiries" replace />} />
        <Route path="/minisite" element={<Navigate to="/mini-site" replace />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/signing" element={<SigningWorkspace />} />
        {/*
          SIG-3.12 — the same as `/quotes/new` above, and for the same reasons.

          The send history lists documents for signing beside quotes. While the form
          is open the menus light its row, and the top bar names the form, as it
          does over a new quote (`navItems.ts`).

          The form takes nothing from the list it used to be drawn over. It
          reads `?duplicate=` and `?library=` off the address itself, and the
          send history only rewrites the query string when its view or a filter
          is changed, keeping what is already there. Leaving refreshes
          `history`, which is what the send history reads, and a document kept
          as a draft is listed there beside the draft quotes.
        */}
        <Route
          path="/signing/new"
          element={
            <>
              <QuotesPage />
              <SigningCompose asDialog />
            </>
          }
        />
        {/* The screen the two above replaced was kept here, reachable by URL,
            while the new one proved itself. It is gone; the URL still answers,
            because a bookmark to a way back should land on the way forward. */}
        <Route path="/signing/legacy" element={<SigningWorkspace />} />
        <Route path="/account" element={<Navigate to="/profile" replace />} />
        {/* The tools hub was a grid of six cards, one per module. The rail lists
            the same six, on every screen — so the hub was a second menu that had
            to be opened to be used. Bookmarks of it go where it went. */}
        <Route path="/tools" element={<Navigate to="/" replace />} />

        {/* Inside the shell, not beside it: a signed-in user who mistypes a path
            or follows a stale link should land somewhere they can navigate out
            of, and the bare version of this screen offers one button. A visitor
            with no session falls to `Protected` above and comes back here after
            signing in. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
